/*eslint new-cap:0 */
import _ from 'underscore';
import Backbone from 'backbone';
import ko from 'knockout';
import pageContext from '@bingads-webui-static-legacy/page-context';
import LanguageSettingView from '@bingads-webui-component-legacy/language-selector/view/index';
import template from '@bingads-webui-campaign-legacy/adgroup-create-advanced/template/steps/settings.pug';
import ViewModel from '@bingads-webui-campaign-legacy/adgroup-create-advanced/viewmodel/adgroup';
import campaignType from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/campaignType';
import CopySettings from '@bingads-webui-campaign-legacy/adgroup-create-advanced/view/copy-adgroup-settings';
import BiddingStrategyView from '@bingads-webui-campaign-legacy/bidding-strategy/view/setting';
import biddingStrategyTypes from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import AdGroupBidType from '@bingads-webui-campaign-legacy/shopping-campaign/view/cpg-adgroup/bid-type';
import showCpgAdGroupType from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/showCpgAdGroupType';
import { CashbackSettings } from '@bingads-webui-campaign/cashback-settings';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const campaignType2sectionMap = () => {
  return {
    'search': ['language', 'automatedBidding'],
    'shopping': ['automatedBidding']
      .concat(showCpgAdGroupType() ? ['cpgAdGroupType'] : []),
  };
};

const inheritFromCampaignLanguageName = null;
const isArabicEnabled = pageContext.Permissions.IsArabicLanguageEnabledInMultipleLanguage;
const isSimplifiedChineseEnabled = pageContext.Permissions.IsSimplifiedChineseLanguageEnabledInMultipleLanguage;
const IsDomesticSimplifiedChineseEnabled = pageContext.Permissions.IsDomesticSimplifiedChineseEnabled;
const isSponsoredPilotEnabled = pageContext.Permissions.IsSPAv2Enabled;
const isSponsoredPromotionsByBrandsEnabled = pageContext.Permissions.dynamic.SponsoredPromotionsByBrands;
const isMranEcpcEnabled = pageContext.Permissions.dynamic.MranEcpc;
const isMranTroasEnabled = pageContext.Permissions.dynamic.MranTroas;
const isManualBiddingDeprecationEnabled = pageContext.Permissions.IsManualBiddingDeprecationEnabled;
const SponsoredCampaignSubType = 11;

const section2viewMap = {
  language: {
    viewFactory: LanguageSettingView,
    options: {
      i18nStrings: {
        Description: i18n.get('AdGroup_Language_Description'),
        SaveWarning: null,
      },
      IsAdsGlobalizationPhase1Enabled: pageContext.Permissions.IsAdsGlobalizationPhase1Enabled,
      IsAdsGlobalizationPhase2Enabled: pageContext.Permissions.IsAdsGlobalizationPhase2Enabled,
      IsAdsGlobalizationPhase6Enabled: pageContext.Permissions.IsAdsGlobalizationPhase6Enabled,
      IsAdsGlobalizationPhase2JapanEnabled: pageContext.Permissions.IsAdsGlobalizationPhase2JapanEnabled,
      IsDomesticSimplifiedChineseEnabled: pageContext.Permissions.IsDomesticSimplifiedChineseEnabled,
      IsAdsGlobalizationPhase9Enabled: pageContext.Permissions.IsAdsGlobalizationPhase9Enabled,
      IsAdsGlobalizationPhase9VIEnabled: pageContext.Permissions.IsAdsGlobalizationPhase9VIEnabled,
    },
  },
  automatedBidding: {
    viewFactory: BiddingStrategyView,
    options: {
      entityType: 'AdGroup',
      biddingStrategyType: biddingStrategyTypes.parent,
    },
  },
  cpgAdGroupType: {
    viewFactory: AdGroupBidType,
  },
};

export default class View extends Backbone.View {
  initialize({
    blockedElement = null,
    isDynamicAdGroupCreation = false,
    showCashbackSettings = false,
  } = {}) {
    this.campaign = pageContext.CurrentCampaign;

    _.extend(section2viewMap.language.options, {
      enableArabic: isArabicEnabled,
      enableSimplifiedChinese: isSimplifiedChineseEnabled,
      IsDomesticSimplifiedChineseEnabled,
    });

    _.extend(section2viewMap.automatedBidding.options, {
      campaignBiddingStrategyType: _.chain(this.campaign).result('BiddingScheme').result('Type').value(),
    });

    // Dynamically put stateful info in the global constant section2viewMap. Otherwise ad group creation won't behave the same under different campaignType + campaignSubTypes
    if (isManualBiddingDeprecationEnabled) {
      _.extend(section2viewMap.automatedBidding.options, {
        campaignType: campaignType(),
        campaignSubType: this.campaign.CampaignSubType,
      });
    }

    if (this.campaign && !_.isEmpty(this.campaign.Languages)) {
      _.extend(section2viewMap.language.options, {
        language: inheritFromCampaignLanguageName,  // for multile language campaign, make 'inherit' the default selection
        withInheritOption: true,
      });
    }

    let sections = campaignType2sectionMap()[campaignType()];

    if (isDynamicAdGroupCreation) {
      // Actually there is no extra control rather than ad group name for dynamic search ad group
      // However I still want to keep this pattern in case in the futher there may be specific controls for DSA ad group
      sections = campaignType2sectionMap().dynamicSearch;
    }

    this.subViews = _.object(sections, _.map(sections, (section) => {
      return new section2viewMap[section].viewFactory(section2viewMap[section].options);
    }));

    if (showCashbackSettings) {
      const ParentLevelCashbackPercent = _.chain(this.campaign)
        .result('CampaignCashback')
        .result('Percent', null)
        .value();
      const ParentLevelCashbackScope = _.chain(this.campaign)
        .result('CampaignCashback')
        .result('Scope')
        .value() || 'None';
      const ParentCouponsEnabled = _.chain(this.campaign)
        .result('CampaignCashback')
        .result('CouponsEnabled', 'None')
        .value();

      this.subViews.cashbackSettings = new CashbackSettings({
        showCashbackBudget: false,
        isCashbackScopeReadonly: campaignType() !== 'shopping',
        showCoupon: campaignType() === 'shopping',
        showEnableCashbackSettingsCheckbox: false,
        showInheritFromParentOption: true,
        CashbackScope: 'None',
        CashbackPercent: null,
        ParentLevelCashbackPercent,
        ParentLevelCashbackScope,
        CouponsEnabled: 'None',
        ParentCouponsEnabled,
        deps: {
          i18n,
          pageContext,
          decimal,
        },
      });
    }

    this.viewModel = new ViewModel({
      campaignName: this.campaign.Name,
    });

    if (this.subViews.language) {
      this.viewModel.Language(this.subViews.language.koModel.language());
      this.subViews.language.koModel.language.subscribe((value) => {
        this.viewModel.Language(value);
        this.trigger('update:language', value);
      });
    } else {
      const defaultLang = _.chain(this.campaign).result('CampaignSettings').first().result('Language', 'English').value();

      this.viewModel.Language(defaultLang);
    }

    this.copySettings = new CopySettings(blockedElement);
    this.copySettings.on('copy.adgroup', this.copyAdGroup.bind(this));
    this.copySettings.on('copy.errors', (errors) => {
      this.viewModel.errors.push(...errors);
    });
  }

  copyAdGroup([adGroup, adGroupCriterions]) {
    if (!_.isEmpty(adGroup)) {
      if (this.subViews.language && adGroup.Language) { // in multiple language, adgroup.language can be null
        this.subViews.language.koModel.language(adGroup.Language);
      }

      if (this.subViews.automatedBidding && !_.isEmpty(adGroup.BiddingScheme)) {
        this.subViews.automatedBidding.update(adGroup.BiddingScheme);
      }
    }

    this.trigger('copy.adgroup', [adGroup, adGroupCriterions]);
  }

  isSponsoredCampaign() {
    return isSponsoredPilotEnabled && this.campaign && this.campaign.CampaignSubType === SponsoredCampaignSubType;
  }

  isSponsoredPromotionsByBrandsCampaign() {
    return isSponsoredPromotionsByBrandsEnabled && this.isSponsoredCampaign() && this.campaign.CampaignCashback && this.campaign.CampaignCashback.IsPromotionsForBrands;
  }

  render() {
    this.$el.append(template({
      i18n,
      subViews: this.subViews,
    }));
    ko.applyBindings(this.viewModel, this.el);

    _.each(this.subViews, (view, section) => {
      this.$(`.${section}-section`).append(view.render().el);
    });

    if (this.subViews.cpgAdGroupType && this.isSponsoredCampaign()) {
      // remove .cpgAdGroupType-section if SPAv2 is enabled for shopping campaign
      this.$('.cpgAdGroupType-section').empty();
    }

    if (this.subViews.automatedBidding && !this.isSponsoredCampaign()) {
      // not show bid strategy container if SPAv2 is enabled for shopping campaign
      this.$('.bidding-strategy__container').empty().html(this.subViews.automatedBidding.render().el);
    }

    this.$('.copy-adgroup-settings').append(this.copySettings.render().$el);

    return this;
  }

  getAdGroup() {
    let adGroupToSave = {
      Name: this.viewModel.Name(),
      Language: this.viewModel.Language(),
      Campaign: {
        Id: this.campaign.Id,
      },
      Medium: 'Search',
      PricingModel: 'CPC',
    };

    if (this.subViews.automatedBidding) {
      if (this.isSponsoredCampaign()) {
        if (!this.isSponsoredPromotionsByBrandsCampaign() && !isMranEcpcEnabled && !isMranTroasEnabled) {
          // default to manual cpc for SPAv2
          // leave it to null (inherited from parent) for SPB
          adGroupToSave.BiddingScheme = {
            Type: 'ManualCpc',
          };
        }
      } else {
        adGroupToSave.BiddingScheme = this.subViews.automatedBidding.getValues();
      }
    }

    if (this.subViews.cpgAdGroupType) {
      if (this.isSponsoredCampaign()) {
        // default to bid value
        adGroupToSave.Settings = [{ BidOption: 'BidValue' }];
      } else {
        adGroupToSave.Settings = [this.subViews.cpgAdGroupType.getState()];
      }
    }

    if (this.subViews.cashbackSettings) {
      _.extend(adGroupToSave, this.subViews.cashbackSettings.getValues());
    }

    return adGroupToSave;
  }

  isAfterSave() {
    if (this.subViews.language) {
      this.subViews.language.koModel.disabled(true);
    }
  }

  validate() {
    return _.every([
      this.viewModel.validate(),
      this.subViews.automatedBidding ? this.subViews.automatedBidding.validate() : true,
      this.subViews.cashbackSettings ? this.subViews.cashbackSettings.validate() : true,
    ]);
  }
}
